/**
 * Maps API snake_case assignment responses to camelCase domain interfaces.
 */
import { AssignmentStatus } from "@/ts/enums/AssignmentStatusEnum";
import type {
  AssignmentApiResponse,
  AssignmentCurriculumConnectionApiResponse,
  AssignmentDetailApiResponse,
  AssignmentSubmissionApiResponse,
  AssignmentAssetApiResponse
} from "@ts/interfaces/assignments/api/assignmentsApiInterface";
import type {
  Assignment,
  AssignmentBlocks,
  AssignmentCurriculumConnection,
  AssignmentDetail,
  AssignmentDeliverable,
  AssignmentDocAsset,
  AssignmentExemption,
  AssignmentFileAsset,
  AssignmentGrade,
  AssignmentResource,
  AssignmentSubmission,
  RubricBlocks,
  SupportGuideBlocks
} from "@ts/interfaces/assignments/assignmentsInterface";

/* ------------------------------------------------------------------ */
/*  Status mapping                                                    */
/* ------------------------------------------------------------------ */

export const mapAssignmentStatus = (
  latestSubmission: AssignmentSubmissionApiResponse | null
): AssignmentStatus => {
  if (!latestSubmission) return AssignmentStatus.TODO;

  const status = latestSubmission.status?.toLowerCase();

  switch (status) {
    case "in progress":
      return AssignmentStatus.TODO;
    case "pending marks":
      return AssignmentStatus.PENDING;
    case "marked":
      return latestSubmission.viewed_at
        ? AssignmentStatus.MARKED
        : AssignmentStatus.MARKS_NOT_AVAILABLE;
    case "exempted":
      return AssignmentStatus.EXEMPTED;
    case "resubmission required":
      return AssignmentStatus.RESUBMISSION_REQUIRED;
    default:
      return AssignmentStatus.TODO;
  }
};

/* ------------------------------------------------------------------ */
/*  Single-field mappers                                              */
/* ------------------------------------------------------------------ */

const mapGrade = (
  grade: AssignmentApiResponse["grade"] | null
): AssignmentGrade => ({
  id: grade?.id ?? 0,
  gradeCode: grade?.grade_code ?? 0,
  grade: grade?.grade ?? 0,
  shortName: grade?.short_name ?? "",
  longName: grade?.long_name ?? "",
  productFamilyId: grade?.product_family_id ?? "",
  year: grade?.year ?? ""
});

const mapCurriculumConnection = (
  items: AssignmentCurriculumConnectionApiResponse[] | null | undefined
): AssignmentCurriculumConnection[] =>
  (items ?? []).map(c => ({
    curriculumCode: c.curriculum_code,
    curriculumDescription: c.curriculum_description
  }));

const mapFileAsset = (asset: AssignmentAssetApiResponse): AssignmentFileAsset => ({
  uuid: asset.uuid,
  assetId: asset.asset_id,
  name: asset.name,
  downloadLink: asset.download_link,
  type: asset.type
});

const mapSubmission = (
  sub: AssignmentSubmissionApiResponse
): AssignmentSubmission => ({
  uuid: sub.uuid,
  submittedOn: sub.submitted_on,
  marks: sub.marks,
  type: sub.type,
  status: sub.status,
  maxMark: sub.max_mark,
  overallComment: sub.overall_comment,
  assignmentName: sub.assignment_name,
  subject: sub.subject,
  viewedAt: sub.viewed_at,
  rejectReasons: sub.reject_reasons ?? [],
  markerAssessmentAssets: (sub.marker_assessment_asset ?? []).map(mapFileAsset),
  studentSubmissionAssets: (sub.student_submission_asset ?? []).map(mapFileAsset)
});

/* ------------------------------------------------------------------ */
/*  List mapping                                                      */
/* ------------------------------------------------------------------ */

export const mapAssignmentList = (
  items: AssignmentApiResponse[]
): Assignment[] =>
  items.map(item => ({
    uuid: item.uuid,
    name: item.name,
    subject: item.subject ?? "",
    term: item.term,
    startWeek: item.start_week,
    submissionWeek: item.submission_week,
    maxMark: item.max_mark,
    highestMarks: item.highest_marks ?? 0,
    markedCount: item.marked_count ?? 0,
    status: mapAssignmentStatus(item.latest_submission),
    description: item.description ?? "",
    purpose: item.purpose ?? null,
    learningOutcome: item.learning_outcome ?? [],
    curriculumConnection: mapCurriculumConnection(item.curriculum_connection),
    grade: mapGrade(item.grade),
    latestSubmission: item.latest_submission
      ? mapSubmission(item.latest_submission)
      : null
  }));

/* ------------------------------------------------------------------ */
/*  Detail mapping                                                    */
/* ------------------------------------------------------------------ */

export const mapAssignmentDetail = (
  data: AssignmentDetailApiResponse
): AssignmentDetail => {
  const assignment: Assignment = {
    uuid: data.assignment.uuid,
    name: data.assignment.name,
    subject: data.assignment.subject ?? "",
    term: data.assignment.term,
    startWeek: data.assignment.start_week,
    submissionWeek: data.assignment.submission_week,
    maxMark: data.assignment.max_mark,
    highestMarks: data.assignment.highest_marks ?? 0,
    markedCount: data.assignment.marked_count ?? 0,
    status: mapAssignmentStatus(data.assignment.latest_submission),
    description: data.assignment.description ?? "",
    purpose: data.assignment.purpose ?? null,
    learningOutcome: data.assignment.learning_outcome ?? [],
    curriculumConnection: mapCurriculumConnection(
      data.assignment.curriculum_connection
    ),
    grade: mapGrade(data.assignment.grade),
    latestSubmission: data.assignment.latest_submission
      ? mapSubmission(data.assignment.latest_submission)
      : null
  };

  const assets: AssignmentDocAsset[] = (data.assets ?? []).map(a => ({
    id: a.id,
    downloadLink: a.download_link,
    fileName: a.file_name,
    fileType: a.file_type,
    size: a.size,
    type: a.type
  }));

  const resources: AssignmentResource[] = (data.resources ?? []).map(r => ({
    uuid: r.uuid,
    downloadLink: r.download_link,
    downloadable: r.downloadable,
    fileName: r.file_name,
    fileType: r.file_type,
    size: r.size,
    type: r.type,
    websiteLink: r.website_link ?? null
  }));

  const deliverables: AssignmentDeliverable[] = (data.deliverables ?? []).map(
    d => ({
      uuid: d.uuid,
      allowLink: d.allow_link === 1,
      allowUpload: d.allow_upload === 1,
      description: d.description,
      mandatory: d.mandatory === 1,
      fileTypes: d.file_type ?? []
    })
  );

  const previousSubmissions: AssignmentSubmission[] = (
    data.previous_submissions ?? []
  ).map(mapSubmission);

  const exemptions: AssignmentExemption[] = (data.exemptions ?? []).map(e => ({
    uuid: e.uuid,
    status: e.status,
    reason: e.reason
  }));

  // The blocks are nested inside data.assignment (not at the top level of the
  // detail response) — the API embeds them in the assignment object itself.
  const assignmentBlocks: AssignmentBlocks | undefined =
    data.assignment.assignment_blocks
      ? {
          assignmentOverview: data.assignment.assignment_blocks.assignment_overview,
          instructions: data.assignment.assignment_blocks.instructions,
          whatToInclude: data.assignment.assignment_blocks.what_to_include,
          whatSuccessLooksLike:
            data.assignment.assignment_blocks.what_success_looks_like
        }
      : undefined;

  const rubricBlocks: RubricBlocks | undefined =
    data.assignment.rubric_blocks?.marking_rubric
      ? { markingRubric: data.assignment.rubric_blocks.marking_rubric }
      : undefined;

  const supportGuideBlocks: SupportGuideBlocks | undefined =
    data.assignment.support_guide_blocks?.support_guide
      ? { supportGuide: data.assignment.support_guide_blocks.support_guide }
      : undefined;

  return {
    assignment,
    assets,
    resources,
    deliverables,
    previousSubmissions,
    exemptions,
    hasExceededAllowedExemptions: data.has_exceeded_allowed_exemptions ?? false,
    assignmentBlocks,
    rubricBlocks,
    supportGuideBlocks,
    assignmentImageUrl: data.assignment_image_url ?? null
  };
};
